import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { forgetPassword } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/auth/forgot-password')({
  head: () =>
    buildHead({
      title: 'Forgot Password',
      description: 'Reset your Prod Battle password.',
      path: '/auth/forgot-password',
      noindex: true,
    }),
  component: ForgotPassword,
});

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await forgetPassword(email);
      // Always show the same success message regardless of whether the
      // address actually exists - the API doesn't tell us either way and
      // we don't want this page to become an account-enumeration oracle.
      setSubmitted(true);
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16 space-y-6">
      <header>
        <h2 className="text-2xl font-bold">Reset your password</h2>
        <p className="text-muted-foreground text-sm">
          Enter your email and we'll send you a link to choose a new password.
        </p>
      </header>

      {submitted ? (
        <div className="space-y-3 text-sm">
          <p>
            If an account exists for <strong>{email}</strong>, a reset link is on its way. The link
            expires in 1 hour.
          </p>
          <p className="text-muted-foreground">
            Didn't get the email? Check spam, or{' '}
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="underline hover:text-foreground"
            >
              try again
            </button>
            .
          </p>
          <p>
            <Link to="/auth/sign-in" className="underline">
              Back to sign in
            </Link>
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
          {err && <p className="text-sm text-red-500">{err}</p>}
          <button
            type="submit"
            disabled={pending}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {pending ? 'Sending...' : 'Send reset link'}
          </button>
          <p className="text-center text-xs text-muted-foreground">
            <Link to="/auth/sign-in" className="underline hover:text-foreground">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </section>
  );
}
